/**
 * WorkflowSessionMessages — shared message rendering components.
 *
 * Contains parseMessageContent, formatContent, MessageBody, ToolCallsSection,
 * and MessageBubble. Exported for use in WorkflowSessionDetail.
 */

import { useState, useCallback } from "react";
import type { SessionMessage } from "./types";
import MessageBody from "./MessageBody";
import ToolCallsSection from "./ToolCallsSection";
import styles from "./workflow.module.scss";

// ── Constants ──────────────────────────────────────────────────────────────

export const TRUNCATE_THRESHOLD = 500;
export const TRUNCATE_PREVIEW = 300;

// ── Parsing & formatting helpers ───────────────────────────────────────────

/**
 * Parse OpenClaw message content that embeds metadata as markdown JSON blocks.
 * Returns the cleaned human-readable text and any extracted sender info.
 */
export const parseMessageContent = (
  content: string
): { text: string; sender?: string; senderId?: string; isSlack?: boolean } => {
  if (!content) return { text: "" };
  let cleaned = content.trim();
  let sender: string | undefined;
  let senderId: string | undefined;

  // Strip OpenClaw metadata blocks: "Conversation info (untrusted metadata):\n```json\n{...}\n```"
  // and "Sender (untrusted metadata):\n```json\n{...}\n```"
  const metaBlockRegex =
    /(?:Conversation info|Sender)\s*\(untrusted metadata\):\s*```json\s*([\s\S]*?)```\s*/g;
  let match: RegExpExecArray | null;
  while ((match = metaBlockRegex.exec(cleaned)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed.sender || parsed.name) {
        sender = parsed.sender || parsed.name;
        senderId = parsed.sender_id || parsed.id;
      }
    } catch {
      // ignore parse errors
    }
  }
  cleaned = cleaned.replace(metaBlockRegex, "").trim();

  // Strip [[reply_to_current]] and similar directives
  cleaned = cleaned.replace(/\[\[[\w_]+\]\]\s*/g, "").trim();

  // Strip Slack-style user mentions like <@U0AHH7W05J7>
  cleaned = cleaned.replace(/<@U[A-Z0-9]+>/g, "").trim();

  // If what remains is pure JSON, try to extract readable content
  if (
    (cleaned.startsWith("{") && cleaned.endsWith("}")) ||
    (cleaned.startsWith("[") && cleaned.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(cleaned);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const readable =
          parsed.text || parsed.content || parsed.message || parsed.body || parsed.output;
        if (typeof readable === "string") {
          return { text: readable.trim(), sender, senderId };
        }
      }
    } catch {
      // Not valid JSON, use as-is
    }
  }

  // Detect Slack origin: Slack user IDs start with U followed by alphanumeric
  const isSlack = senderId ? /^U[A-Z0-9]+$/.test(senderId) : false;

  return { text: cleaned, sender, senderId, isSlack };
};

/** Format inline content: bold, inline code, links, and line breaks. */
const formatInlineContent = (text: string, keyOffset: number): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const lines = text.split("\n");
  lines.forEach((line, lineIdx) => {
    if (lineIdx > 0) {
      parts.push(<br key={`br-${keyOffset}-${lineIdx}`} />);
    }
    const inlineRegex = /(\*\*(.+?)\*\*)|(`([^`]+)`)|((https?:\/\/[^\s<]+))/g;
    let inlineLastIndex = 0;
    let inlineMatch: RegExpExecArray | null;

    while ((inlineMatch = inlineRegex.exec(line)) !== null) {
      if (inlineMatch.index > inlineLastIndex) {
        parts.push(line.slice(inlineLastIndex, inlineMatch.index));
      }
      if (inlineMatch[2]) {
        parts.push(
          <strong key={`b-${keyOffset}-${lineIdx}-${inlineMatch.index}`}>{inlineMatch[2]}</strong>
        );
      } else if (inlineMatch[4]) {
        parts.push(
          <code
            key={`ic-${keyOffset}-${lineIdx}-${inlineMatch.index}`}
            className={styles.inlineCode}
          >
            {inlineMatch[4]}
          </code>
        );
      } else if (inlineMatch[5]) {
        parts.push(
          <a
            key={`a-${keyOffset}-${lineIdx}-${inlineMatch.index}`}
            href={inlineMatch[5]}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.messageLink}
          >
            {inlineMatch[5]}
          </a>
        );
      }
      inlineLastIndex = inlineMatch.index + inlineMatch[0].length;
    }
    if (inlineLastIndex < line.length) {
      parts.push(line.slice(inlineLastIndex));
    }
  });
  return parts;
};

/** Simple formatting: render code blocks, bold, and links. */
export const formatContent = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...formatInlineContent(text.slice(lastIndex, match.index), parts.length));
    }
    parts.push(
      <pre key={`code-${match.index}`} className={styles.codeBlock}>
        <code>{match[2]}</code>
      </pre>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...formatInlineContent(text.slice(lastIndex), parts.length));
  }

  return parts.length > 0 ? parts : [text];
};

/** Get display role label. senderName overrides for user messages. */
export const getRoleLabel = (role: SessionMessage["role"], senderName?: string): string => {
  switch (role) {
    case "user":
      return senderName || "You";
    case "assistant":
      return "Altimate AI";
    case "tool":
    case "toolResult":
      return "System";
    case "system":
      return "System";
    default:
      return role;
  }
};

// ── Components ─────────────────────────────────────────────────────────────

/** Single message bubble that extracts sender name from content metadata. */
export const MessageBubble = ({ message }: { message: SessionMessage }) => {
  const [senderName, setSenderName] = useState<string | undefined>();
  const [isSlack, setIsSlack] = useState(false);

  const handleParsed = useCallback(
    (sender?: string, slack?: boolean) => {
      if (sender && sender !== senderName) setSenderName(sender);
      if (slack !== undefined) setIsSlack(slack);
    },
    [senderName]
  );

  return (
    <div className={`${styles.messageBubble} ${styles[message.role]}`}>
      <div className={styles.messageHeader}>
        <span className={styles.messageRole}>
          {message.role === "assistant" && (
            <span className={styles.botIndicator} title="Altimate AI">
              &#9679;{" "}
            </span>
          )}
          {getRoleLabel(message.role, senderName)}
          {message.role === "assistant" && <span className={styles.botTag}>[bot]</span>}
          {message.role === "user" && isSlack && (
            <span className={styles.viaSlackTag}>(via Slack)</span>
          )}
        </span>
        {message.created_at && (
          <span className={styles.messageTimestamp}>{formatTime(message.created_at)}</span>
        )}
      </div>
      <MessageBody content={message.content} onParsed={handleParsed} />
      {message.tool_calls ? <ToolCallsSection toolCalls={message.tool_calls} /> : null}
    </div>
  );
};

/** Format time for display in message bubble. */
const formatTime = (iso: string) => {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
